import { useState } from 'react';
import {
  Container,
  Title,
  Table,
  Badge,
  Button,
  Group,
  Text,
  Avatar,
  Loader,
  Alert,
  Modal,
  Select,
  Collapse,
  Anchor,
  Box,
  Grid,
  Paper,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconEye,
  IconEyeOff,
  IconChevronDown,
  IconChevronUp,
  IconExternalLink,
  IconBuildingStore,
  IconStar,
  IconUser,
  IconMapPin,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { Navigate, Link } from 'react-router-dom';
import api from '../../utils/api';

// Define types based on the API response
interface StoreOwner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string;
}

interface Store {
  id: string;
  name: string;
  imageUrl?: string;
  websiteUrl?: string;
  facebookUrl?: string;
  averageReview: number;
  totalReviews: number;
  activeListings: number;
  status: 'ACTIVE' | 'HIDDEN';
  address?: {
    street: string;
    street2?: string;
    city: string;
    province: string;
    postalCode: string;
  };
  owner: StoreOwner;
}

interface StoresResponse {
  stores: Store[];
}

const AdminStoresPage = () => {
  const { isAdmin } = useAuth();
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<'ACTIVE' | 'HIDDEN'>('ACTIVE');
  const [expandedStores, setExpandedStores] = useState<string[]>([]);
  const queryClient = useQueryClient();

  // Fetch all stores
  const { data, isLoading, error } = useQuery<StoresResponse>({
    queryKey: ['adminStores'],
    queryFn: async () => {
      const response = await api.get('/api/siteadmin/stores');
      return response.data;
    },
  });

  // Update store status mutation
  const updateStoreMutation = useMutation({
    mutationFn: async ({
      storeId,
      status,
    }: {
      storeId: string;
      status: 'ACTIVE' | 'HIDDEN';
    }) => {
      const response = await api.put('/api/siteadmin/stores', {
        storeId,
        status,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch the stores query
      queryClient.invalidateQueries({ queryKey: ['adminStores'] });
      setIsModalOpen(false);
    },
  });

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleStatusChange = (store: Store) => {
    setSelectedStore(store);
    setNewStatus(store.status);
    setIsModalOpen(true);
  };

  const handleUpdateStatus = () => {
    if (selectedStore && newStatus) {
      updateStoreMutation.mutate({
        storeId: selectedStore.id,
        status: newStatus,
      });
    }
  };

  const toggleStoreExpansion = (storeId: string) => {
    setExpandedStores((prev) =>
      prev.includes(storeId)
        ? prev.filter((id) => id !== storeId)
        : [...prev, storeId]
    );
  };

  if (isLoading) {
    return (
      <Container size="lg" py="xl" style={{ textAlign: 'center' }}>
        <Loader size="xl" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load stores. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Title order={1} mb="lg">
        Store Management
      </Title>

      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 40 }}></Table.Th>
            <Table.Th>Store</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data?.stores &&
            data?.stores.map((store) => (
              <>
                <Table.Tr key={store.id}>
                  <Table.Td>
                    <Button
                      variant="subtle"
                      size="xs"
                      p={0}
                      onClick={() => toggleStoreExpansion(store.id)}
                    >
                      {expandedStores.includes(store.id) ? (
                        <IconChevronUp size={16} />
                      ) : (
                        <IconChevronDown size={16} />
                      )}
                    </Button>
                  </Table.Td>
                  <Table.Td>
                    <Group>
                      <Avatar src={store.imageUrl} radius="xl" size="md" />
                      <div>
                        <Text fw={500}>{store.name}</Text>
                        <Text size="xs" c="dimmed">
                          {store.activeListings} active listings
                        </Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={store.status === 'ACTIVE' ? 'green' : 'gray'}
                      variant="filled"
                    >
                      {store.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      variant="light"
                      color={store.status === 'ACTIVE' ? 'red' : 'green'}
                      onClick={() => handleStatusChange(store)}
                      leftSection={
                        store.status === 'ACTIVE' ? (
                          <IconEyeOff size={16} />
                        ) : (
                          <IconEye size={16} />
                        )
                      }
                      size="xs"
                    >
                      {store.status === 'ACTIVE'
                        ? 'Hide (disable)'
                        : 'Activate'}
                    </Button>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td colSpan={4} p={0}>
                    <Collapse in={expandedStores.includes(store.id)}>
                      <Paper withBorder p="md" radius={0}>
                        <Grid>
                          <Grid.Col span={6}>
                            <Group mb="xs">
                              <IconUser size={16} />
                              <Text fw={500}>Owner Information</Text>
                            </Group>
                            <Group ml="md" mb="xs">
                              <Avatar
                                src={store.owner.profileImage}
                                radius="xl"
                                size="sm"
                              />
                              <div>
                                <Text size="sm">{`${store.owner.firstName} ${store.owner.lastName}`}</Text>
                                <Text size="xs" c="dimmed">
                                  {store.owner.email}
                                </Text>
                              </div>
                            </Group>
                          </Grid.Col>

                          <Grid.Col span={6}>
                            <Group mb="xs">
                              <IconStar size={16} />
                              <Text fw={500}>Reviews</Text>
                            </Group>
                            <Text ml="md" size="sm">
                              {store.totalReviews > 0 ? (
                                <>
                                  Rating: {store.averageReview.toFixed(1)} (
                                  {store.totalReviews} reviews)
                                </>
                              ) : (
                                'No reviews yet'
                              )}
                            </Text>
                          </Grid.Col>

                          {store.address && (
                            <Grid.Col span={6}>
                              <Group mb="xs">
                                <IconMapPin size={16} />
                                <Text fw={500}>Address</Text>
                              </Group>
                              <Box ml="md">
                                <Text size="sm">{store.address.street}</Text>
                                {store.address.street2 && (
                                  <Text size="sm">{store.address.street2}</Text>
                                )}
                                <Text size="sm">
                                  {store.address.city}, {store.address.province}{' '}
                                  {store.address.postalCode}
                                </Text>
                              </Box>
                            </Grid.Col>
                          )}

                          <Grid.Col span={6}>
                            <Group mb="xs">
                              <IconBuildingStore size={16} />
                              <Text fw={500}>Store Details</Text>
                            </Group>
                            <Box ml="md">
                              {store.websiteUrl && (
                                <Text size="sm" mb="xs">
                                  Website:{' '}
                                  <Anchor
                                    href={store.websiteUrl}
                                    target="_blank"
                                  >
                                    {store.websiteUrl}
                                  </Anchor>
                                </Text>
                              )}
                              {store.facebookUrl && (
                                <Text size="sm" mb="xs">
                                  Facebook:{' '}
                                  <Anchor
                                    href={store.facebookUrl}
                                    target="_blank"
                                  >
                                    {store.facebookUrl}
                                  </Anchor>
                                </Text>
                              )}
                              <Button
                                component={Link}
                                to={`/store/${store.name}/`}
                                target="_blank"
                                variant="light"
                                size="xs"
                                mt="xs"
                                leftSection={<IconExternalLink size={14} />}
                              >
                                View Store Listings
                              </Button>
                            </Box>
                          </Grid.Col>
                        </Grid>
                      </Paper>
                    </Collapse>
                  </Table.Td>
                </Table.Tr>
              </>
            ))}
        </Table.Tbody>
      </Table>

      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Update Store Status"
        centered
      >
        {selectedStore && (
          <>
            <Text mb="md">
              You are about to change the status of{' '}
              <strong>{selectedStore?.name}</strong>.
            </Text>

            <Select
              label="New Status"
              value={newStatus}
              onChange={(value) => setNewStatus(value as 'ACTIVE' | 'HIDDEN')}
              data={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'HIDDEN', label: 'Hidden' },
              ]}
              mb="md"
            />

            <Group justify="flex-end" mt="xl">
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button
                color={newStatus === 'ACTIVE' ? 'green' : 'red'}
                onClick={handleUpdateStatus}
                loading={updateStoreMutation.isPending}
                leftSection={
                  newStatus === 'ACTIVE' ? (
                    <IconEye size={16} />
                  ) : (
                    <IconEyeOff size={16} />
                  )
                }
              >
                {newStatus === 'ACTIVE' ? 'Activate Store' : 'Hide Store'}
              </Button>
            </Group>
          </>
        )}
      </Modal>
    </Container>
  );
};

export default AdminStoresPage;
